import React, {
  ComponentProps,
  ComponentPropsWithRef,
  FC,
  useMemo,
} from 'react';

import { CSSProp } from 'styled-components';

import { Box } from '@adsk/alloy-react-box';
import Button from '@adsk/alloy-react-button';
import { noop } from '@adsk/alloy-react-helpers';
import Menu, {
  DropdownButton,
  MenuItem,
  MenuItemType,
} from '@adsk/alloy-react-menu';
import Tooltip from '@adsk/alloy-react-tooltip';

import getPluginKey from './helpers/getPluginKey';
import { useTranslation } from './i18n';
import TypeButtonLabel from './TypeButtonLabel';
import { CommonReferencePickerPlugin } from './types/ReferencePickerPlugin';

const tooltipOffset = { mainAxis: -5, crossAxis: -5 };

type CustomMenuItemProps = ComponentProps<typeof MenuItem> & {
  tooltipContent?: React.ReactNode;
  disabled?: boolean;
};

interface ReferencePickerTypeButtonProps {
  plugins: CommonReferencePickerPlugin[];
  openPickerModal: (plugin: CommonReferencePickerPlugin) => void;
  children: React.ReactNode;
  loadingPlugins?: boolean;
  container?: React.RefObject<HTMLElement> | null;
}

type OptionType = MenuItemType<string> & {
  tooltipContent?: React.ReactNode;
  style?: CSSProp;
};

const CustomMenuItem = React.forwardRef<HTMLButtonElement, CustomMenuItemProps>(
  ({ tooltipContent, ...props }, ref) => {
    const { t } = useTranslation();
    return (
      <Box style={props.style}>
        <Tooltip
          content={
            props.disabled
              ? tooltipContent || t('DISABLED_NO_PERMISSIONS')
              : null
          }
          offset={tooltipOffset}
        >
          <MenuItem
            ref={ref}
            {...props}
            style={[
              {
                ':disabled': {
                  pointerEvents: 'none',
                },
              },
            ]}
          />
        </Tooltip>
      </Box>
    );
  },
);

CustomMenuItem.displayName = 'CustomMenuItem';

const CustomMenu = React.forwardRef<
  HTMLDivElement,
  ComponentProps<typeof Menu>
>((props, ref) => (
  <Menu
    ref={ref}
    {...props}
    renderItem={(props) => <CustomMenuItem {...props} />}
  />
));

CustomMenu.displayName = 'CustomMenu';

const hasMoreThanOnePluginInGroup = (
  plugin: CommonReferencePickerPlugin,
  plugins: CommonReferencePickerPlugin[],
) =>
  plugins.filter(
    ({ nestedOptions }) =>
      nestedOptions && nestedOptions.key === plugin.nestedOptions?.key,
  ).length > 1;

const createOrUpdateNestedOption = (
  options: OptionType[],
  nestedOptions: NonNullable<CommonReferencePickerPlugin['nestedOptions']>,
  option: OptionType,
) => {
  const parentOption = options.find(
    (option) => nestedOptions.key === option.key,
  );

  const nestedCategory = {
    key: nestedOptions.nestedCategoryKey,
    itemOptions: [option],
    multiSelection: false,
    showSelection: false,
  };

  if (!parentOption) {
    // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array built within useMemo
    options.push({
      key: nestedOptions.key,
      label: nestedOptions.label,
      renderIcon: nestedOptions.renderIcon,
      disabled: false,
      nested: [nestedCategory],
    });
  } else {
    if (!parentOption.nested) return;
    const currentCategoryOption = parentOption.nested.find(
      (item) => item && item.key === nestedOptions.nestedCategoryKey,
    );
    if (currentCategoryOption) {
      currentCategoryOption.itemOptions = [
        ...currentCategoryOption.itemOptions,
        option,
      ];
    } else {
      parentOption.nested = [...parentOption.nested, nestedCategory];
    }
  }
};

const ReferencePickerTypeButton: FC<ReferencePickerTypeButtonProps> = ({
  plugins,
  openPickerModal,
  children,
  loadingPlugins,
  container,
}) => {
  const options = useMemo(() => {
    const options: OptionType[] = [];
    plugins.forEach((plugin) => {
      const disabled = !!plugin.disablePickerWithMessage;
      const tooltipContent = plugin.disablePickerWithMessage;
      const showIcon =
        !plugin.nestedOptions?.hideSubMenuItemIcon ||
        !hasMoreThanOnePluginInGroup(plugin, plugins);

      const option: OptionType = {
        key: getPluginKey(plugin),
        label: (
          <TypeButtonLabel
            plugin={plugin}
            disabled={disabled}
            showIcon={showIcon}
          />
        ),
        disabled,
        tooltipContent,
      };

      const hasCustomOptionStyle = !!plugin.CustomOptionStyle;
      if (hasCustomOptionStyle) {
        option.style = plugin.CustomOptionStyle;
      }
      if (
        plugin.nestedOptions &&
        hasMoreThanOnePluginInGroup(plugin, plugins)
      ) {
        createOrUpdateNestedOption(options, plugin.nestedOptions, option);
      } else {
        // eslint-disable-next-line better-mutation/no-mutating-methods -- local accumulator array built within useMemo
        options.push(option);
      }
    });
    return options;
  }, [plugins]);

  if (loadingPlugins) {
    return (
      <Button
        onClick={noop}
        disabled
        loading
        data-testid="ReferencePickerView__add-reference-button"
      >
        {children}
      </Button>
    );
  }
  if (!plugins.length) {
    return null;
  }
  if (plugins.length === 1) {
    const disabled = !!plugins[0].disablePickerWithMessage;
    const tooltipContent = plugins[0].disablePickerWithMessage;
    return (
      <Tooltip content={tooltipContent}>
        <Button
          onClick={() => openPickerModal(plugins[0])}
          data-testid="ReferencePickerView__add-reference-button"
          disabled={disabled}
        >
          {children}
        </Button>
      </Tooltip>
    );
  }

  const keyToPlugin = plugins.reduce(
    (acc: Record<string, CommonReferencePickerPlugin>, plugin) => {
      acc[getPluginKey(plugin)] = plugin;
      return acc;
    },
    {},
  );

  return (
    <Box marginLeft="auto">
      <DropdownButton
        container={container}
        options={options}
        onChange={([value]) => openPickerModal(keyToPlugin[value])}
        renderContent={(props: ComponentPropsWithRef<typeof Menu>) => (
          <CustomMenu {...props} />
        )}
        data-testid="ReferencePickerTypeButton"
        value={[]}
      >
        {children}
      </DropdownButton>
    </Box>
  );
};

export default ReferencePickerTypeButton;
